
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Wallet, Laptop, Building2, Lightbulb, Users, Search, Layout, LineChart, Briefcase, Bot, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const MiguelProfile = () => {
  const user = {
    name: "Miguel Escobedo",
    role: "iSPAI Chief Product Officer",
    email: "miguel.escobedo@ispai.io",
    solutionBounties: 12,
    domains: ["Product Strategy", "UX Research", "Design Systems", "User Experience", "Product Design", "Cross-functional Leadership", "Customer Experience", "Product Development", "Omnichannel Platforms", "Team Leadership"],
    industries: [{
      name: "Fintech",
      icon: Wallet
    }, {
      name: "SaaS",
      icon: Laptop
    }, {
      name: "Enterprise",
      icon: Building2
    }, {
      name: "Innovation",
      icon: Lightbulb
    }, {
      name: "Team Building",
      icon: Users
    }, {
      name: "UX Research",
      icon: Search
    }],
    confirmedSolutions: 45,
    confirmedKnowledgeBases: 82,
    tokenBalance: 280,
    bio: "Miguel Escobedo is the Chief Product Officer at iSPAI, bringing over 17 years of expertise in product design, strategy, and leadership. A proven innovator, Miguel has consistently driven meaningful customer-centered product development, achieving impressive gains in acquisition, retention, and adoption metrics across his career. He has built and led cross-functional teams of 3 to 30+, delivering results in e-commerce, fintech, SaaS, and omnichannel platforms.\n\nPrior to iSPAI, Miguel held leadership roles such as Director of User Experience & Research at ActBlue and Director of Product Design at WP Engine, where he scaled design teams, launched transformative design systems, and aligned product strategies with customer needs. At USAA and PayPal, he spearheaded data-driven and user-focused initiatives, including the design of omnichannel onboarding systems, scalable design systems, and groundbreaking products like PayPal Working Capital and Venmo mobile payments.\n\nMiguel's expertise spans product strategy, UX research, design systems, and cross-functional leadership, with a track record of creating solutions that combine innovative design with practical execution. His ability to bridge technical, creative, and strategic perspectives makes him a vital contributor to iSPAI's mission to democratize knowledge and deliver scalable, impactful products."
  };

  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/06ffa061-1d96-42f2-b61f-7ae9243333ff.png"
                    imageAlt="Miguel Escobedo"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/06ffa061-1d96-42f2-b61f-7ae9243333ff.png" 
                        alt="Miguel Escobedo"
                      />
                      <AvatarFallback>ME</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="bg-white text-black hover:bg-gray-100">
                          BIO
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <div className="space-y-2">
                          <h4 className="text-lg font-semibold">Miguel Escobedo</h4>
                          <p className="text-sm text-gray-600 whitespace-pre-line">{user.bio}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="sm" className="bg-white text-black hover:bg-gray-100">
                      NW
                    </Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map(industry => {
                      const Icon = industry.icon;
                      return <div key={industry.name} className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg">
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>;
                    })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link to="/solution-bounties/miguel" className="text-sm text-blue-600 hover:text-blue-800">
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.6} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map(domain => <Badge key={domain} variant="outline" className="border-2 border-black text-black">
                    {domain}
                  </Badge>)}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>;
};

export default MiguelProfile;
